import { Pressable, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';

export interface BackButtonProps {
  /**
   * The route to navigate to when back is pressed. If provided, this will be used
   * instead of router.back() to ensure consistent navigation. Should be a route path
   * like '/(tabs)/portfolios/123'
   */
  to?: string;
  /**
   * Optional fallback route to navigate to if router.back() is not available
   * or if canGoBack is false. Only used if 'to' is not provided.
   * Should be a route path like '/(tabs)/portfolios/123'
   */
  fallbackRoute?: string;
  /**
   * Custom label text (default: "← Back")
   */
  label?: string;
  /**
   * Custom style for the container
   */
  style?: ViewStyle;
  /**
   * Custom style for the text
   */
  textStyle?: TextStyle;
  /**
   * Whether to use router.back() instead of the provided route.
   * Default: false (uses provided route if available)
   */
  useHistory?: boolean;
}

export function BackButton({
  to,
  fallbackRoute,
  label = '← Back',
  style,
  textStyle,
  useHistory = false,
}: BackButtonProps) {
  const router = useRouter();
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const handlePress = () => {
    // If useHistory is true, prefer browser history
    if (useHistory) {
      const canGoBack = typeof router.canGoBack === 'function' ? router.canGoBack() : true;
      if (canGoBack) {
        router.back();
        return;
      }
    }

    // If 'to' route is provided, use it (this is the preferred behavior)
    // Use replace to avoid adding to history stack
    if (to) {
      router.replace(to as any);
      return;
    }

    // If fallbackRoute is provided, use it
    if (fallbackRoute) {
      router.replace(fallbackRoute as any);
      return;
    }

    // Last resort: try router.back()
    const canGoBack = typeof router.canGoBack === 'function' ? router.canGoBack() : true;
    if (canGoBack) {
      router.back();
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      style={[styles.container, style]}
    >
      <Text style={[styles.text, textStyle]}>{label}</Text>
    </Pressable>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      alignSelf: 'flex-start',
      paddingVertical: utils.spacing.sm,
      paddingHorizontal: utils.spacing.md,
      marginBottom: utils.spacing.md,
    },
    text: {
      fontSize: utils.typography.sizes.base,
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

